/**
 * Transforms an array of ancestors by filtering out falsy values and mapping each ancestor object.
 * If the cleanName property is missing, it sends an error to TrackJS.
 * @param {Array} ancestors - The array of ancestor objects.
 * @returns {Array} - The transformed array of ancestors.
 */
const transformAncestors = (ancestors) => {
  if (!ancestors || !Array.isArray(ancestors)) {
    return [];
  }

  return ancestors
    .filter((ea) => ea)
    .map(({cleanName, name, id, ...ancestor}) => {
      if (!cleanName && typeof window !== 'undefined' && window.TrackJS) {
        // Send error to TrackJS
        window.TrackJS.track(`Missing cleanName for ancestor "${name}" with id ${id}`);
      }

      return {...ancestor, id, cleanName: cleanName || name, name};
    });
};

const SORT_ORDER = {
  ASCENDING: true,
  DESCENDING: false,
};

/**
 * Sorts an array of ancestors by the number of words in their names.
 *
 * @param {Array} ancestors - The array of ancestors to be sorted.
 * @param {string} [order='ASCENDING'] - The sorting order. Possible values are 'ASCENDING' or 'DESCENDING'.
 * @returns {Array} - The sorted array of ancestors.
 */
const sortAncestorsByWords = (ancestors, order = SORT_ORDER.ASCENDING) => {
  if (!ancestors || !Array.isArray(ancestors)) {
    return [];
  }

  return ancestors.sort((a, b) => {
    const aWords = a.name.split(' ').length;
    const bWords = b.name.split(' ').length;

    if (order === SORT_ORDER.DESCENDING) {
      return bWords - aWords;
    }

    return aWords - bWords;
  });
};

/**
 * Recapitulates the event name pieces by replacing certain pieces with objects from the sortedObj array.
 * If cleanObjNames is provided, it also checks for matches within the cleanObjNames array.
 * @param {string} eventName - The original event name.
 * @param {Array} eventNamePieces - The array of event name pieces.
 * @param {Array} sortedObj - The array of objects used for replacement.
 * @param {Array} cleanObjNames - The array of clean object names used for additional replacement.
 * @returns {Array} - The updated array of event name pieces.
 */
const recapEventNamePieces = (eventName, eventNamePieces, sortedObj, cleanObjNames) => {
  sortedObj.forEach((ea) => {
    let index = eventName.indexOf(ea.cleanName);
    if (index !== -1) {
      eventNamePieces[index] = ea;
    } else if (cleanObjNames) {
      const [cleanName] = cleanObjNames.filter((name) => ea.cleanName.includes(name));
      index = eventName.indexOf(cleanName);
      if (index !== -1) {
        eventNamePieces[index] = {...ea, cleanName};
      }
    }
  });

  return eventNamePieces;
};

export {transformAncestors, sortAncestorsByWords, recapEventNamePieces, SORT_ORDER};
